import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Leaf, Droplets, Recycle, Users, TrendingUp, Shield, ArrowRight, Zap, Factory, Beaker, Sparkles, Footprints } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const NuvoraDetail = () => {
  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Header />
      <main style={{ paddingTop: '180px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'var(--bg-primary)',
          padding: '80px 7.6923%',
          position: 'relative'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <Link to="/" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--brand-primary)',
              textDecoration: 'none',
              fontSize: '16px',
              marginBottom: '30px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(-5px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
            >
              <ArrowLeft size={20} />
              Back to Solutions
            </Link>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }} className="detail-grid">
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_biz-innovations/artifacts/ny87boz7_generated-image%20%2811%29.png" 
                  alt="Nuvora" 
                  style={{ height: '144px', objectFit: 'contain', marginBottom: '30px' }}
                />
                <h1 className="display-large" style={{ marginBottom: '20px' }}>
                  Channeling <span style={{ color: 'var(--brand-primary)' }}>Tomorrow's Food Innovations</span>
                </h1>
                <p className="body-large" style={{ marginBottom: '30px' }}>
                  Redefining the Future of Food through Sustainability and Innovation
                </p>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1556910103-1c02745aae4d" 
                  alt="Food Innovation" 
                  style={{ width: '100%', height: '400px', objectFit: 'cover' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              padding: '60px',
              background: 'rgba(0, 255, 209, 0.05)',
              border: '1px solid var(--brand-primary)'
            }}>
              <p className="body-large" style={{ lineHeight: '1.8' }}>
                Nuvora stands at the intersection of innovation, sustainability, and culinary evolution. We bring together next-generation food and kitchen solutions that transform the way we eat, manage waste, and experience sustainability.
              </p>
              <p className="body-large" style={{ lineHeight: '1.8', marginTop: '20px' }}>
                As a channel partner to some of the most pathbreaking innovators in the Food & Beverage industry, Nuvora serves as the bridge between inventive brands and forward-thinking organizations committed to creating a cleaner, smarter planet.
              </p>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="display-medium" style={{ marginBottom: '30px' }}>
              Our <span style={{ color: 'var(--brand-primary)' }}>Mission</span>
            </h2>
            <p className="body-large" style={{ maxWidth: '1000px', margin: '0 auto', lineHeight: '1.8' }}>
              To be the preferred channel for cutting-edge F&B innovations that inspire sustainable living, healthier choices, and efficient kitchen ecosystems—across homes, institutions, and commercial kitchens.
            </p>
          </div>
        </section>

        {/* Food Innovations */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '20px', textAlign: 'center' }}>
              Food <span style={{ color: 'var(--brand-primary)' }}>Innovations</span>
            </h2>
            <p className="body-medium" style={{ textAlign: 'center', marginBottom: '60px', color: 'var(--text-muted)' }}>
              Revolutionary products transforming food convenience
            </p>

            <div style={{ display: 'grid', gap: '40px' }}>
              {/* Ambient Grated Coconut */}
              <div style={{
                padding: '50px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--brand-primary)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
              >
                <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'rgba(0, 255, 209, 0.1)',
                    border: '1px solid var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Leaf size={40} color="var(--brand-primary)" />
                  </div>
                  <div>
                    <h3 className="heading-1" style={{ marginBottom: '16px' }}>Ambient Grated Coconut</h3>
                    <p className="body-medium" style={{ lineHeight: '1.8' }}>
                      Revolutionizing food convenience, this innovation preserves freshly grated coconut at ambient temperature with a 2-year shelf life, maintaining taste, aroma, and nutrition—without refrigeration or preservatives.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Food Waste Management Solutions */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '20px', textAlign: 'center' }}>
              Food Waste <span style={{ color: 'var(--brand-primary)' }}>Management Solutions</span>
            </h2>
            <p className="body-medium" style={{ textAlign: 'center', marginBottom: '60px', color: 'var(--text-muted)' }}>
              On-site processing solutions that eliminate landfill dependency
            </p>

            <div style={{ display: 'grid', gap: '40px' }}>
              {/* Digestor */}
              <div style={{
                padding: '50px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--brand-primary)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
              >
                <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'rgba(0, 255, 209, 0.1)',
                    border: '1px solid var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Recycle size={40} color="var(--brand-primary)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 className="heading-1" style={{ marginBottom: '16px' }}>Digestor – The All-in-One Bio-Digester</h3>
                    <p className="body-medium" style={{ lineHeight: '1.8', marginBottom: '20px' }}>
                      Uses patented bio-microbes and enzymes to convert all food waste into water (and CO₂) within 24 hours—no solid waste remains. Proprietary blend digests the entire food waste stream including meats, bones, bread, fruit, and dairy.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Eliminates labor-intensive hauling and storage</span>
                      </div>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Reduces landfill and methane emissions</span>
                      </div>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Works 24/7 with real-time monitoring</span>
                      </div>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Greywater output is drain-safe</span>
                      </div>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Payback typically within 1–3 years</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dewaterer */}
              <div style={{
                padding: '50px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--brand-primary)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
              >
                <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'rgba(0, 255, 209, 0.1)',
                    border: '1px solid var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Zap size={40} color="var(--brand-primary)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 className="heading-1" style={{ marginBottom: '16px' }}>Dewaterer – The Volume Slayer</h3>
                    <p className="body-medium" style={{ lineHeight: '1.8', marginBottom: '20px' }}>
                      A compact, standalone system that physically separates moisture from waste, making it compact and easy to handle. Achieves up to 80% volume reduction in minutes—perfect for kitchens with high daily turnover.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Reduces trips and costs for waste removal</span>
                      </div>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Minimizes odor and pest attraction</span>
                      </div>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Compact footprint, plug-and-play installation</span>
                      </div>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Safe and hassle-free operation</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* KWM */}
              <div style={{
                padding: '50px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--brand-primary)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
              >
                <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'rgba(0, 255, 209, 0.1)',
                    border: '1px solid var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Sparkles size={40} color="var(--brand-primary)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 className="heading-1" style={{ marginBottom: '16px' }}>KWM – Kitchen Waste Management Bioproduct</h3>
                    <p className="body-medium" style={{ lineHeight: '1.8', marginBottom: '20px' }}>
                      Microbial mix designed for converting kitchen waste into valuable organic manure right at the source. Rapidly accelerates composting from 21 days to 15 days—supporting eco-friendly waste handling for hotels, food courts, and food processing units.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Converts waste into valuable manure</span>
                      </div>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Controls and eliminates odor</span>
                      </div>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Simple, low-maintenance application</span>
                      </div>
                      <div style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">Works with OWC systems</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Comparison Table */}
            <div style={{ marginTop: '60px' }}>
              <h3 className="heading-1" style={{ marginBottom: '30px', textAlign: 'center' }}>
                Why Advanced Food Waste Solutions?
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '16px', background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)', textAlign: 'left', color: 'var(--brand-primary)' }}>Challenge</th>
                      <th style={{ padding: '16px', background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)', textAlign: 'left', color: 'var(--brand-primary)' }}>Old Approach</th>
                      <th style={{ padding: '16px', background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)', textAlign: 'left', color: 'var(--brand-primary)' }}>With Digestor/Dewaterer</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>High disposal costs</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>Manual collection/hauling</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--brand-primary)' }}>On-site processing & water</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Odor & pests</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>Open bins, slow removal</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--brand-primary)' }}>Fast, sealed, odor-free</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Landfill volume</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>Sent to dump</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--brand-primary)' }}>No solids; water discharge</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Staff time/labor</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>Daily cleaning, running</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--brand-primary)' }}>Automated, minimal handling</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Wastewater Management - Bio Solutions */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '20px', textAlign: 'center' }}>
              Wastewater <span style={{ color: 'var(--brand-primary)' }}>Bio Solutions</span>
            </h2>
            <p className="body-medium" style={{ textAlign: 'center', marginBottom: '20px', color: 'var(--text-muted)' }}>
              Natural Bio Solutions — Powered by Superweld, Distributed by VDM
            </p>
            <p className="body-medium" style={{ textAlign: 'center', marginBottom: '60px', maxWidth: '900px', margin: '0 auto 60px' }}>
              The SUPER Series & BIOFRESH are advanced microbial and enzyme-based solutions that clean naturally—replacing harsh chemicals across water, wastewater, and hygiene systems.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px' }}>
              {/* OXY-SUPER */}
              <div style={{
                padding: '40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--brand-primary)';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <Beaker size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                <h3 className="heading-2" style={{ marginBottom: '12px', color: 'var(--brand-primary)' }}>OXY-SUPER</h3>
                <p className="body-small" style={{ marginBottom: '16px', fontWeight: '600' }}>Dissolved Oxygen Booster & Odor Eliminator</p>
                <p className="body-medium" style={{ marginBottom: '16px' }}>
                  Bio-powder that enhances microbial growth to speed up waste degradation in ponds and STPs.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, fontSize: '14px' }}>
                  <li style={{ marginBottom: '8px', color: 'var(--text-muted)' }}>• Reduces BOD, COD, and foul gas emissions</li>
                  <li style={{ marginBottom: '8px', color: 'var(--text-muted)' }}>• Clarifies water and halts algae growth</li>
                  <li style={{ color: 'var(--text-muted)' }}>• Safe for aquatic ecosystems</li>
                </ul>
              </div>

              {/* SUPERFLOW */}
              <div style={{
                padding: '40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--brand-primary)';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <Droplets size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                <h3 className="heading-2" style={{ marginBottom: '12px', color: 'var(--brand-primary)' }}>SUPERFLOW</h3>
                <p className="body-small" style={{ marginBottom: '16px', fontWeight: '600' }}>The Kitchen Drain Hero</p>
                <p className="body-medium" style={{ marginBottom: '16px' }}>
                  Enzymatic bio-liquid that digests oils, fats, and organic waste naturally to prevent blockages.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, fontSize: '14px' }}>
                  <li style={{ marginBottom: '8px', color: 'var(--text-muted)' }}>• Reduces manual cleaning and odor</li>
                  <li style={{ marginBottom: '8px', color: 'var(--text-muted)' }}>• Improves kitchen hygiene & compliance</li>
                  <li style={{ color: 'var(--text-muted)' }}>• Safe, food-grade, cost-effective</li>
                </ul>
              </div>

              {/* SUPER24 */}
              <div style={{
                padding: '40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--brand-primary)';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <Factory size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                <h3 className="heading-2" style={{ marginBottom: '12px', color: 'var(--brand-primary)' }}>SUPER24</h3>
                <p className="body-small" style={{ marginBottom: '16px', fontWeight: '600' }}>STP Performance Turbocharger</p>
                <p className="body-medium" style={{ marginBottom: '16px' }}>
                  Enriched microbial powder for balanced MLSS and faster degradation in sewage treatment plants.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, fontSize: '14px' }}>
                  <li style={{ marginBottom: '8px', color: 'var(--text-muted)' }}>• Lowers chemical usage</li>
                  <li style={{ marginBottom: '8px', color: 'var(--text-muted)' }}>• Improves effluent quality & compliance</li>
                  <li style={{ color: 'var(--text-muted)' }}>• Reduces sludge disposal costs</li>
                </ul>
              </div>

              {/* BIOFRESH */}
              <div style={{
                padding: '40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--brand-primary)';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <Sparkles size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                <h3 className="heading-2" style={{ marginBottom: '12px', color: 'var(--brand-primary)' }}>BIOFRESH</h3>
                <p className="body-small" style={{ marginBottom: '16px', fontWeight: '600' }}>Restroom Drain Reviver</p>
                <p className="body-medium" style={{ marginBottom: '16px' }}>
                  Active bacteria that produce enzymes to dissolve urine salts and body fats into water-soluble compounds.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, fontSize: '14px' }}>
                  <li style={{ marginBottom: '8px', color: 'var(--text-muted)' }}>• Complete odor elimination</li>
                  <li style={{ marginBottom: '8px', color: 'var(--text-muted)' }}>• Safe for fixtures, zero corrosion</li>
                  <li style={{ color: 'var(--text-muted)' }}>• Ensures free flow and freshness</li>
                </ul>
              </div>
            </div>

            {/* Bio Solutions Impact Table */}
            <div style={{ marginTop: '60px' }}>
              <h3 className="heading-1" style={{ marginBottom: '30px', textAlign: 'center' }}>
                Measurable Campus Impact
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '16px', background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)', textAlign: 'left', color: 'var(--brand-primary)' }}>Challenge</th>
                      <th style={{ padding: '16px', background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)', textAlign: 'left', color: 'var(--brand-primary)' }}>Traditional Fix</th>
                      <th style={{ padding: '16px', background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)', textAlign: 'left', color: 'var(--brand-primary)' }}>Bio-Solution Advantage</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>STP Odor</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>Chemical dosing</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--brand-primary)' }}>Natural microbial balance</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Drain Clogs</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>Manual pumping</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--brand-primary)' }}>Enzyme digestion</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Urinal Smell</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>Acid cleaners</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--brand-primary)' }}>Biofresh freshness</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Algae Growth</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>Temporary removal</td>
                      <td style={{ padding: '16px', border: '1px solid var(--border-subtle)', color: 'var(--brand-primary)' }}>Long-term balance</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Products Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
              Featured <span style={{ color: 'var(--brand-primary)' }}>Products</span>
            </h2>

            <div style={{ display: 'grid', gap: '40px' }}>
              {/* KleenClad */}
              <div style={{
                padding: '50px',
                background: 'rgba(0, 255, 209, 0.08)',
                border: '2px solid var(--brand-primary)',
                transition: 'all 0.3s',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '30px',
                  background: 'var(--brand-primary)',
                  color: 'var(--bg-solid)',
                  padding: '4px 16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px'
                }}>
                  FEATURED PRODUCT
                </div>
                <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'rgba(0, 255, 209, 0.2)',
                    border: '2px solid var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Shield size={40} color="var(--brand-primary)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 className="heading-1" style={{ marginBottom: '16px', color: 'var(--brand-primary)' }}>KleenClad – Advanced Antimicrobial Cladding</h3>
                    <p className="body-medium" style={{ lineHeight: '1.8', marginBottom: '20px' }}>
                      Hygienic wall protection tailored for commercial kitchens, designed to elevate food safety standards. Powered by Polygiene Biomaster Silver Ion Technology that inhibits 99.99% of bacterial growth including Listeria, Salmonella, and E. coli.
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0, marginBottom: '24px' }}>
                      <li style={{ marginBottom: '10px', paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">IP66 rated for cold storage & humid environments</span>
                      </li>
                      <li style={{ marginBottom: '10px', paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">HACCP, FDA, and EU compliant surfaces</span>
                      </li>
                      <li style={{ paddingLeft: '24px', position: 'relative' }}>
                        <CheckCircle2 size={16} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                        <span className="body-small">100% recyclable with BREEAM & LEED certifications</span>
                      </li>
                    </ul>
                    <Link to="/kleenclad" className="btn-primary" style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      textDecoration: 'none'
                    }}>
                      Learn More About KleenClad
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Dycem */}
              <div style={{
                padding: '50px',
                background: 'rgba(0, 255, 209, 0.08)',
                border: '2px solid var(--brand-primary)',
                transition: 'all 0.3s',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '30px',
                  background: 'var(--brand-primary)',
                  color: 'var(--bg-solid)',
                  padding: '4px 16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px'
                }}>
                  FEATURED PRODUCT
                </div>
                <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'rgba(0, 255, 209, 0.2)',
                    border: '2px solid var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Footprints size={40} color="var(--brand-primary)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 className="heading-1" style={{ marginBottom: '16px', color: 'var(--brand-primary)' }}>Dycem – Premium Antimicrobial Flooring</h3>
                    <p className="body-medium" style={{ lineHeight: '1.8', marginBottom: '20px' }}>
                      Premium antimicrobial flooring solution designed for commercial kitchens. Captures up to 99.9% of floor particulates and 75% from the air, preventing cross-contamination at the floor level. Integrated Biomaster Silver Ion Technology provides 24/7 protection against E. coli, Listeria, and Salmonella.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                      <div>
                        <h4 className="body-small" style={{ fontWeight: '600', marginBottom: '12px', color: 'var(--brand-primary)' }}>Key Features</h4>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                          <li style={{ marginBottom: '8px', paddingLeft: '24px', position: 'relative' }}>
                            <CheckCircle2 size={14} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                            <span className="body-small">99.9% particulate capture vs 73% for sticky mats</span>
                          </li>
                          <li style={{ marginBottom: '8px', paddingLeft: '24px', position: 'relative' }}>
                            <CheckCircle2 size={14} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                            <span className="body-small">Washable and reusable design</span>
                          </li>
                          <li style={{ paddingLeft: '24px', position: 'relative' }}>
                            <CheckCircle2 size={14} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                            <span className="body-small">Static dissipative (10⁸ ohms)</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="body-small" style={{ fontWeight: '600', marginBottom: '12px', color: 'var(--brand-primary)' }}>Applications</h4>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                          <li style={{ marginBottom: '8px', paddingLeft: '24px', position: 'relative' }}>
                            <CheckCircle2 size={14} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                            <span className="body-small">Entrances to prep and processing areas</span>
                          </li>
                          <li style={{ marginBottom: '8px', paddingLeft: '24px', position: 'relative' }}>
                            <CheckCircle2 size={14} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                            <span className="body-small">Gowning rooms and transition points</span>
                          </li>
                          <li style={{ paddingLeft: '24px', position: 'relative' }}>
                            <CheckCircle2 size={14} color="var(--brand-primary)" style={{ position: 'absolute', left: 0, top: '4px' }} />
                            <span className="body-small">Corridors and material transfer zones</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
                      <span style={{ padding: '6px 12px', background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)', fontSize: '12px', color: 'var(--brand-primary)' }}>ISO 9001</span>
                      <span style={{ padding: '6px 12px', background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)', fontSize: '12px', color: 'var(--brand-primary)' }}>ISO 14001</span>
                      <span style={{ padding: '6px 12px', background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)', fontSize: '12px', color: 'var(--brand-primary)' }}>RoHS</span>
                      <span style={{ padding: '6px 12px', background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)', fontSize: '12px', color: 'var(--brand-primary)' }}>REACH</span>
                      <span style={{ padding: '6px 12px', background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)', fontSize: '12px', color: 'var(--brand-primary)' }}>1-Year Warranty</span>
                    </div>
                    <Link to="/#contact" className="btn-primary" style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      textDecoration: 'none'
                    }}>
                      Request Site Survey
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Partner with Nuvora */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
              Why Partner with <span style={{ color: 'var(--brand-primary)' }}>Nuvora</span>
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '40px'
            }}>
              <div style={{
                padding: '40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--brand-primary)';
                e.currentTarget.style.transform = 'translateY(-10px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <CheckCircle2 size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                <h3 className="heading-2" style={{ marginBottom: '16px' }}>Exclusive Representation</h3>
                <p className="body-medium">
                  We serve as the official channel partner for some of the most forward-looking F&B innovators.
                </p>
              </div>

              <div style={{
                padding: '40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--brand-primary)';
                e.currentTarget.style.transform = 'translateY(-10px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <TrendingUp size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                <h3 className="heading-2" style={{ marginBottom: '16px' }}>Market Access and Growth</h3>
                <p className="body-medium">
                  Our network bridges product creators with retail, HORECA, and institutional channels across India.
                </p>
              </div>

              <div style={{
                padding: '40px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--brand-primary)';
                e.currentTarget.style.transform = 'translateY(-10px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <Leaf size={40} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                <h3 className="heading-2" style={{ marginBottom: '16px' }}>Sustainability Focus</h3>
                <p className="body-medium">
                  Every innovation we represent contributes to a cleaner ecosystem and better consumer well-being.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              padding: '80px 60px',
              background: 'rgba(0, 255, 209, 0.05)',
              border: '2px solid var(--brand-primary)',
              textAlign: 'center'
            }}>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Join the <span style={{ color: 'var(--brand-primary)' }}>Nuvora Network</span>
              </h2>
              <p className="body-large" style={{ marginBottom: '40px', maxWidth: '800px', margin: '0 auto 40px' }}>
                Be part of a movement where innovation meets purpose. Whether you are an F&B brand seeking representation, a business looking to source next-gen solutions, or an investor in sustainable ventures—Nuvora connects the right ideas to the right opportunities.
              </p>
              <p className="body-large" style={{ marginBottom: '40px', fontWeight: '600', color: 'var(--brand-primary)' }}>
                Let's co-create the future of food and sustainability.
              </p>
              <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/#contact" className="btn-primary">
                  Partner with Us
                </Link>
                <Link to="/#contact" className="btn-secondary">
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        @media (max-width: 768px) {
          .detail-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default NuvoraDetail;
